import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Button, Icon, Modal } from "semantic-ui-react";

import { DBBCreateContractForm, DBB_CONTRACT_TYPES, getDefaultTyreSets, toContractCustomer } from "components/DBB/components/DBBCreateContractModal/components";
import "components/DBB/components/DBBCreateContractModal/DBBCreateContractModal.scss";
import { useDBBContractOptions } from "components/DBB/components/DBBCreateContractModal/hooks";
import { getContractFormErrors, toCreateContractPayload } from "components/DBB/components/DBBCreateContractModal/utils";
import { useDBBCreateContractMutation } from "components/DBB/hooks/useDBBCreateContractMutation";
import { createFormHelper } from "lib/Form";
import { CUSTOMER_TYPES } from "models";

import type { DBBContractFormPayload } from "components/DBB/components/DBBCreateContractModal/components";
import type { Car } from "models";
import type { CarCustomerInfo } from "modules/CarDetails/hooks";

type DBBCreateContractModalProps = {
  open: boolean;
  car: Car;
  customers?: CarCustomerInfo;
  onClose: () => void;
};

const CONTRACT_FORM_ID = "dbb-create-contract";
const FormHelper = createFormHelper<DBBContractFormPayload>();

export const DBBCreateContractModal = ({ car, customers, open, onClose }: DBBCreateContractModalProps) => {
  const { t } = useTranslation();

  const defaultValues = useMemo<DBBContractFormPayload>(
    () => ({
      contract_type: customers?.contractor?.is_lease_company ? DBB_CONTRACT_TYPES.Lease : DBB_CONTRACT_TYPES.Private,
      pickup_by_dbb: false,
      lease_company_id: null,
      storage_location_id: null,
      driver: toContractCustomer(customers?.driver, CUSTOMER_TYPES.Driver),
      owner: toContractCustomer(customers?.owner, CUSTOMER_TYPES.Owner),
      car: { reg_nr: car.reg_nr, vin_nr: car.vin_nr, make: car.make, model: car.model, model_year: null, mileage: customers?.current_km || null },
      tyre_sets: getDefaultTyreSets(car)
    }),
    [car, customers]
  );

  const { mutate: createDBBContract, isPending } = useDBBCreateContractMutation();
  useDBBContractOptions(car);

  const isFormInvalid = (values: DBBContractFormPayload) => {
    const { hasIncompleteCar, hasIncompleteCustomer, hasMissingLeaseCompany, hasMissingStorageLocation, hasIncompleteTyreSets } = getContractFormErrors(values);

    if (hasIncompleteCar || hasIncompleteCustomer || hasMissingLeaseCompany || hasMissingStorageLocation) return true;
    if (hasIncompleteTyreSets) {
      toast.error(t("v8_dbb_incomplete_tyre_sets", "Pick two complete tyre sets, each one with a tyre on all four positions"));
      return true;
    }
    return false;
  };

  const handleSubmit = (values: DBBContractFormPayload) => {
    if (isFormInvalid(values)) return;

    createDBBContract(toCreateContractPayload(car, values), { onSuccess: onClose });
  };

  return (
    <Modal size="fullscreen" open={open} onClose={onClose} closeOnDimmerClick={false} className="DBBCreateContractModal">
      <Modal.Header>{t("v8_create_dbb_contract", "Create DBB contract")}</Modal.Header>
      <Modal.Content scrolling className="contract-modal-content">
        <FormHelper.Form onSubmit={handleSubmit} defaultValues={defaultValues} formProps={{ id: CONTRACT_FORM_ID }}>
          <DBBCreateContractForm car={car} customers={customers} />
        </FormHelper.Form>
      </Modal.Content>

      <Modal.Actions className="contract-form-actions">
        <Button className="light" type="button" onClick={onClose}>
          {t("v8_cancel", "Cancel")}
          <Icon className="xmark" />
        </Button>
        <Button color="green" type="submit" form={CONTRACT_FORM_ID} disabled={isPending} loading={isPending}>
          {t("v8_save", "Save")}
          <Icon className="check" />
        </Button>
      </Modal.Actions>
    </Modal>
  );
};
